/**
 * Time Utilities
 *
 * Utility functions for consistent time formatting across the application.
 * All times should be displayed in 12-hour format with AM/PM.
 */

/**
 * Convert 24-hour time format to 12-hour format with AM/PM
 * @param {string} time24 - Time in 24-hour format (e.g., "18:35", "11:18")
 * @returns {string} Time in 12-hour format (e.g., "6:35 PM", "11:18 AM")
 */
export const convertTo12HourFormat = (time24) => {
  if (!time24) return '';
  
  // Handle different time formats
  let timeStr = time24.toString().trim();
  
  // If it already contains AM/PM, return as is
  if (timeStr.includes('AM') || timeStr.includes('PM') || timeStr.includes('am') || timeStr.includes('pm')) {
    return timeStr;
  }
  
  // Extract hours and minutes from various formats
  let hours, minutes;
  
  // Handle HH:MM format
  if (timeStr.includes(':')) {
    const parts = timeStr.split(':');
    hours = parseInt(parts[0], 10);
    minutes = parts[1] || '00';
  }
  // Handle HHMM format (like 1835)
  else if (timeStr.length === 4) {
    hours = parseInt(timeStr.substring(0, 2), 10);
    minutes = timeStr.substring(2, 4);
  }
  // Handle HMM format (like 935 for 9:35)
  else if (timeStr.length === 3) {
    hours = parseInt(timeStr.substring(0, 1), 10);
    minutes = timeStr.substring(1, 3);
  }
  // Handle single or double digit hours
  else {
    hours = parseInt(timeStr, 10);
    minutes = '00';
  }
  
  // Validate hours and minutes
  if (isNaN(hours) || hours < 0 || hours > 23) {
    return time24; // Return original if invalid
  }
  
  // Ensure minutes is two digits
  minutes = minutes.toString().padStart(2, '0');
  
  // Convert to 12-hour format
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${displayHours}:${minutes} ${period}`;
};

/**
 * Get current time in 12-hour format
 * @returns {string} Current time in 12-hour format
 */
export const getCurrentTime12Hour = () => {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes().toString().padStart(2, '0');
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${displayHours}:${minutes} ${period}`;
};

/**
 * Format time from various sources to 12-hour format
 * @param {string|Date} timeSource - Time from various sources
 * @returns {string} Formatted time in 12-hour format
 */
export const formatTime12Hour = (timeSource) => {
  if (!timeSource) return '';
  
  // If it's a Date object
  if (timeSource instanceof Date) {
    const hours = timeSource.getHours();
    const minutes = timeSource.getMinutes().toString().padStart(2, '0');
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    
    return `${displayHours}:${minutes} ${period}`;
  }
  
  // If it's a string, try to convert
  return convertTo12HourFormat(timeSource);
};

/**
 * Get time from multiple sources with priority order
 * @param {Object} sources - Object containing different time sources
 * @returns {string} Best available time in 12-hour format
 */
export const getTimeFromSources = (sources) => {
  const { pickupTime, timeParam, pickupTimeParam, travelTimeParam } = sources;

  // Priority order: pickupTime -> timeParam -> pickupTimeParam -> travelTimeParam
  const timeValue = pickupTime || timeParam || pickupTimeParam || travelTimeParam;

  if (!timeValue) return "Time not specified";

  // Convert to 12-hour format
  return convertTo12HourFormat(timeValue);
};

/**
 * Safely decode time parameter from URL
 * @param {string} timeParam - The time parameter from URL
 * @returns {string} Decoded time string
 */
export const decodeTimeParam = (timeParam) => {
  if (!timeParam) return null;
  try {
    return decodeURIComponent(timeParam);
  } catch (e) {
    return timeParam; // Return original if decoding fails
  }
};
